import { useState } from 'react';
import { rafraichit } from '../api/rafraichissement';
import { donneesPersonnellesApi } from '../api/resources';
import { telechargeJson } from '../lib/sauvegarde';
import { C } from '../theme/palette';
import { Btn } from './ui/Btn.jsx';

/** Nom du fichier remis : « donnees-client-famille-morin-2026-10-07.json ». */
function nomDuFichier(sujet, nom, etabliLe) {
  const jour = /^\d{4}-\d{2}-\d{2}/.exec(etabliLe || '');
  const base = String(nom || sujet)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return 'donnees-' + sujet + '-' + (base || sujet) + '-' + (jour ? jour[0] : 'export') + '.json';
}

/**
 * Droit d'accès et droit à l'effacement, sur la fiche d'un employé ou d'un
 * client, pour la direction.
 *
 * <p>Le serveur dressait l'état des données d'une personne et savait rendre
 * sa fiche anonyme, sans qu'aucun écran ne le propose : une demande d'accès
 * ou d'effacement restait sans réponse. L'état se télécharge, prêt à être
 * remis ; l'effacement est irréversible et se confirme en toutes lettres. Le
 * compte rendu dit ce qui a été effacé et ce que la loi impose de garder.
 *
 * @param {object} props propriétés du composant
 * @param {'employe'|'client'} props.sujet nature de la personne
 * @param {number|string} props.id identifiant de la fiche
 * @param {string} [props.nom] nom de la personne, pour le fichier et la confirmation
 * @returns {JSX.Element|null} le bloc, ou rien tant que la fiche n'est pas enregistrée
 */
export function DonneesPersonnellesFiche({ sujet, id, nom }) {
  const [etat, setEtat] = useState('repos');
  const [compteRendu, setCompteRendu] = useState(null);
  const [erreur, setErreur] = useState('');
  // Une fiche encore locale n'existe pas au serveur : rien à exporter.
  if (typeof id !== 'number') return null;

  const exporte = async () => {
    setErreur('');
    try {
      const donnees =
        sujet === 'client'
          ? await donneesPersonnellesApi.client(id)
          : await donneesPersonnellesApi.employe(id);
      telechargeJson(donnees, nomDuFichier(sujet, nom, donnees?.etabliLe));
    } catch (e) {
      setErreur(e?.message || 'L’état des données n’a pas pu être dressé.');
    }
  };

  const efface = async () => {
    if (
      !window.confirm(
        'Effacer les données personnelles de ' +
          (nom || (sujet === 'client' ? 'ce client' : 'cet employé')) +
          ' ?\n\nLa fiche est rendue anonyme : nom, coordonnées et identifiants disparaissent, ' +
          'et cette action est IRRÉVERSIBLE. Les pièces que la loi impose de conserver dix ans ' +
          '(factures, décomptes de salaire, documents émis) restent, et le compte rendu les nommera.',
      )
    )
      return;
    setErreur('');
    setEtat('envoi');
    try {
      const rendu =
        sujet === 'client'
          ? await donneesPersonnellesApi.effaceClient(id)
          : await donneesPersonnellesApi.effaceEmploye(id);
      setCompteRendu(rendu);
      setEtat('fait');
      // La fiche à l'écran porte encore le nom : la base, elle, l'a oublié.
      rafraichit();
    } catch (e) {
      setEtat('repos');
      setErreur(e?.message || 'L’effacement a échoué.');
    }
  };

  return (
    <div
      style={{
        marginTop: 10,
        border: '1px solid ' + C.border,
        borderRadius: 10,
        padding: '10px 12px',
        fontSize: 9.5,
        lineHeight: 1.5,
      }}
    >
      <div style={{ fontWeight: 800, marginBottom: 3 }}>🗂 Données personnelles (LPD)</div>
      <div style={{ color: C.muted, marginBottom: 8 }}>
        Droit d’accès : l’état de ses données, prêt à lui être remis. Droit à l’effacement : la
        fiche devient anonyme, les pièces à conserver dix ans restent.
      </div>
      {compteRendu ? (
        <div role="status" style={{ color: C.ink }}>
          <div style={{ fontWeight: 800, color: C.green }}>✓ Données effacées</div>
          {(compteRendu.champsEffaces || []).length > 0 && (
            <div>Effacé : {compteRendu.champsEffaces.join(', ')}.</div>
          )}
          {(compteRendu.retenus || []).map((r) => (
            <div key={r.famille} style={{ color: C.muted }}>
              Conservé : {r.nombre} {r.famille.toLowerCase()} — {r.motif}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <Btn label="⬇ Exporter ses données" sm ghost color={C.navy} onClick={exporte} />
          <Btn
            label={etat === 'envoi' ? 'Effacement…' : '🗑 Effacer ses données'}
            sm
            ghost
            color={C.red}
            disabled={etat === 'envoi'}
            onClick={efface}
          />
        </div>
      )}
      {erreur && (
        <div role="alert" style={{ color: C.red, marginTop: 6 }}>
          {erreur}
        </div>
      )}
    </div>
  );
}
